// Phase 56 chunk 5 — Per-customer duplicate banner.
//
// Shown at the top of the customer detail page when the customer is part
// of a duplicate cluster. Calls ``GET /customers/{id}/duplicate-candidates``
// once on mount. Renders nothing when there are no candidates.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, GitMerge } from "lucide-react";

export default function CustomerDuplicateBanner({ customerId }) {
  const [cluster, setCluster] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!customerId) { setLoaded(true); return; }
      try {
        const r = await api.get(`/customers/${customerId}/duplicate-candidates`);
        if (!cancelled) setCluster(r.data?.cluster || null);
      } catch {
        // banner is purely informational; swallow errors silently
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [customerId]);

  if (!loaded || !cluster) return null;

  const otherCount = Math.max(0, cluster.customers.length - 1);
  if (otherCount === 0) return null;

  const allIds = cluster.customers.map((c) => c.id).join(",");

  return (
    <div
      className="bg-amber-50 border border-amber-300 rounded-[8px] px-4 py-3 flex items-start justify-between gap-3 flex-wrap mb-4"
      data-testid="customer-duplicate-banner"
    >
      <div className="flex items-start gap-3 min-w-0">
        <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-bold text-amber-900">
            This customer may share data with {otherCount} other customer{otherCount === 1 ? "" : "s"}.
          </div>
          <div className="text-xs text-amber-800/80 mt-0.5">
            Matched on: {cluster.signals.map((s) => s.split(":")[0]).join(", ")}.
            Review the cluster and pick a survivor.
          </div>
        </div>
      </div>
      <Link to={`/customers/merge?ids=${encodeURIComponent(allIds)}`} className="shrink-0">
        <button
          className="inline-flex items-center gap-1.5 text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white rounded-[6px] px-3 py-2 transition-colors"
          data-testid="customer-duplicate-banner-cta"
        >
          <GitMerge className="h-4 w-4" /> Review & merge
        </button>
      </Link>
    </div>
  );
}
